import { History } from 'history';

import { PageJourney } from '@nykaa/data-layer/store/action';
import { getErrorString } from '@nykaa/data-layer/store/helpers';
import {
  pageTypes,
  pageNamePrefix,
  PageRootTypes,
  SiteNavigation,
  siteSubNavigationPrefix,
} from '@nykaa/data-layer/constants';
import { pushEvent } from '@nykaa/data-layer/utils';
import { getStockString } from '@nykaa/product-card-shared/utils';
import {
  queryStringObjectFromUrl,
  appendQueryStringToUrl,
} from 'shared/helpers/utils/url';
import { YOUTUBE_EMBED_URL } from 'shared/constants/productPage';
import {
  DESCRIPTION_TAB,
  INGREDIENTS_TAB,
  HOW_TO_USE_TAB,
  MINIMUM_DESCRIPTION_LENGTH,
} from 'shared/constants/productPage';
import {
  Product,
  CategoryLevels,
  Variant,
  ManufactureData,
  PrimaryCategories,
  CohortSaleTemplate,
  FetchProductParams,
} from './types';

interface ProductDataLayer {
  categoryLevels: CategoryLevels;
  childId?: string;
  id: string;
  image?: string;
  inStock: boolean;
  stockStatus: string;
  discount: number;
  mrp: number;
  name: string;
  offerPrice: number;
  sku: string;
  brandName: string;
  packSize?: string;
  slug?: string;
  rating?: number;
  productType?: string;
  reviewCount: string;
  ratingCount: number;
  variantType: string;
  categoryLevel: PrimaryCategories;
  tags?: string[];
  variantCount?: number;
  selectedVariantName?: string;
  cohortSaleTemplate?: CohortSaleTemplate | null;
  disablePriveProduct?: boolean;
}

export interface TabHeaders {
  [key: string]: number;
}

interface TabObject {
  id: string;
  value: string;
}
export interface TabsData {
  [key: number]: TabObject;
}

export const getDataLayerForProduct = (
  productData: Product | null,
  errorCode: number | null,
  params: FetchProductParams
) => {
  if (!productData) {
    return {
      pageName: getErrorString(errorCode),
      pageType: pageTypes.PRODUCT,
    };
  }

  const {
    id,
    inStock,
    mrp,
    name,
    offerPrice,
    selectedVariantId,
    sku,
    variants,
    slug,
    parentMedia,
    categoryLevels,
    offers,
    brandName,
    packSize,
    rating,
    productType,
    primaryCategories,
    discount,
    reviewCount,
    ratingCount,
    variantType,
    tags,
    selectedVariantName,
    cohortSaleTemplate,
    earlyAccessData,
  } = productData;
  const { disablePriveProduct } = earlyAccessData || {};
  let product: ProductDataLayer = {
    categoryLevels,
    id,
    image: parentMedia[0]?.url,
    inStock: inStock,
    stockStatus: getStockString(inStock),
    mrp,
    name,
    offerPrice,
    sku,
    brandName,
    packSize,
    rating,
    productType,
    slug,
    categoryLevel: primaryCategories,
    discount,
    reviewCount,
    ratingCount,
    variantType,
    tags,
    variantCount: variants.length,
    selectedVariantName,
    cohortSaleTemplate,
    disablePriveProduct,
  };
  let desiredSlug: string = slug;
  // TODO: PI-957 remove this
  // if (selectedVariantId && variants) {
  //   const selectedSKU = variants[selectedVariantId];
  if (selectedVariantId && variants.length > 0) {
    const selectedSKUIndex = variants.findIndex(
      (variant) => variant.childId === selectedVariantId
    );
    const selectedSKU = variants[selectedSKUIndex];

    product = {
      ...product,
      childId: selectedVariantId,
      image: selectedSKU?.media[0]?.url,
      inStock: selectedSKU?.inStock,
      mrp: selectedSKU?.mrp,
      name: selectedSKU?.name,
      offerPrice: selectedSKU?.offerPrice,
      sku: selectedSKU?.sku,
      packSize: selectedSKU?.packSize,
      slug: selectedSKU?.slug,
      selectedVariantName: selectedVariantName,
      cohortSaleTemplate: selectedSKU?.cohortSaleTemplate,
      disablePriveProduct,
    };
    desiredSlug = selectedSKU?.slug;
  }
  const differentialPrice = cohortSaleTemplate?.price || offerPrice;
  const differentialDiscount = cohortSaleTemplate?.discount || discount;
  const differentialPriceTempId = product?.cohortSaleTemplate?.templateId;

  return {
    pageName: `${pageNamePrefix.PRODUCT}${desiredSlug}`,
    pageType: pageTypes.PRODUCT,
    product,
    hasOffer: offers?.length > 0 ? 'yes' : 'no',
    analyticsSdkData: {
      pdpProductId: id,
      pdpProductName: name,
    },
    ...(params.enableDiffPricing &&
      product.cohortSaleTemplate && {
        isPdpDifferentialPricing: true,
        differentialPrice,
        differentialDiscount,
        differentialPriceTempId,
      }),
  };
};

export const removeTabCharsFromString = (str = '') => {
  if (str) {
    return str.replace(/\\t/g, '');
  }
  return str;
};

export const stripScriptTags = (str = {}) => {
  const stringifiedStr = JSON.stringify(str);
  return JSON.parse(stringifiedStr.replace(/<script>.*<\/script>/gi, ''));
};

export const removeNewLineCharsFromString = (str = '') => {
  if (str) {
    return str.replace(/\\n/g, '');
  }
  return str;
};

export const getLegalInfoForPDP = (
  data: ManufactureData,
  countryOfManufacture: string
) => {
  let legalInfoHtmlString = '';
  const {
    originOfCountryName,
    manufacturerName,
    manufacturerAddress,
    packerName,
    packerAddress,
    importerName,
    importerAddress,
    countryOfOriginLabel,
    manufacturerNameLabel,
    manufacturerAddressLabel,
    importerNameLabel,
    importerAddressLabel,
    packerNameLabel,
    packerAddressLabel,
  } = data;

  if (originOfCountryName) {
    const countryName = originOfCountryName.replace(/(\\n|\\t|\\r)/g, '');
    legalInfoHtmlString += `<br><p class="mrp-n-expiry">${countryOfOriginLabel}&nbsp; <p class="content-info">${countryName}</p></p><br>`;
  }

  if (manufacturerName) {
    const name = manufacturerName.replace(/(\\n|\\t|\\r)/g, '');
    legalInfoHtmlString += `<p class="mrp-n-expiry">${manufacturerNameLabel}&nbsp; <p class="content-info">${name}</p></p>`;
  }
  if (manufacturerAddress) {
    const address = manufacturerAddress.replace(/(\\n|\\t|\\r)/g, '');
    legalInfoHtmlString += `<p class="mrp-n-expiry">${manufacturerAddressLabel}&nbsp; <p class="content-info">${address}</p></p><br>`;
  }
  if (countryOfManufacture) {
    const country = countryOfManufacture.replace(/(\\n|\\t|\\r)/g, '');
    legalInfoHtmlString += `<p class="mrp-n-expiry">Country of Manufacture:&nbsp; <p class="content-info">${country}</p></p><br>`;
  }
  if (importerName) {
    const importerNameValue = importerName.replace(/(\\n|\\t|\\r)/g, '');
    legalInfoHtmlString += `<p class="mrp-n-expiry">${importerNameLabel}&nbsp; <p class="content-info">${importerNameValue}</p></p>`;
  }
  if (importerAddress) {
    const importerAddressValue = importerAddress.replace(/(\\n|\\t|\\r)/g, '');
    legalInfoHtmlString += `<p class="mrp-n-expiry">${importerAddressLabel}&nbsp; <p class="content-info">${importerAddressValue}</p></p><br>`;
  }
  if (packerName) {
    const packerNameValue = packerName.replace(/(\\n|\\t|\\r)/g, '');
    legalInfoHtmlString += `<p class="mrp-n-expiry">${packerNameLabel}&nbsp; <p class="content-info">${packerNameValue}</p></p>`;
  }
  if (packerAddress) {
    const packerAddressValue = packerAddress.replace(/(\\n|\\t|\\r)/g, '');
    legalInfoHtmlString += `<p class="mrp-n-expiry">${packerAddressLabel}&nbsp; <p class="content-info">${packerAddressValue}</p></p>`;
  }

  return legalInfoHtmlString;
};

export const extractProductJourneyData = (
  queryString: string
): PageJourney | null => {
  const queryParams = new URLSearchParams(queryString);
  const root = queryParams.get('root');
  const searchItem = queryParams.get('searchItem');

  const journeyData: PageJourney = {};

  switch (root) {
    case PageRootTypes.SEARCH:
      journeyData.siteNavigation = SiteNavigation.SEARCH;
      journeyData.siteSubNavigation = `${siteSubNavigationPrefix.SEARCH}${searchItem}`;
      break;

    case PageRootTypes.CAV_PD:
      journeyData.siteNavigation = SiteNavigation.PRODUCT;
      journeyData.siteSubNavigation = siteSubNavigationPrefix.PRODUCT_CAV;
      break;

    case PageRootTypes.CAB_PD:
      journeyData.siteNavigation = SiteNavigation.PRODUCT;
      journeyData.siteSubNavigation = siteSubNavigationPrefix.PRODUCT_CAB;
      break;

    default:
      return null;
  }

  return journeyData;
};

export const embeddedVideoUrl = (url: string): string => {
  const queryStringObject = queryStringObjectFromUrl(url);
  const videoId = queryStringObject.v;
  return `${YOUTUBE_EMBED_URL}${videoId}`;
};

export const handleSkuIdInUrl = (
  history: History,
  currentSku: Variant,
  parentId: string
) => {
  const { search: queryString, pathname } = history.location;
  const urlParams = new URLSearchParams(queryString);
  const { childId: currentSkuId } = currentSku;
  const QUERY_PARAM_KEY_FOR_SKU = 'skuId';
  const skuId = urlParams.get(QUERY_PARAM_KEY_FOR_SKU);
  let shouldUpdateUrl = false;

  if (skuId) {
    if (skuId !== currentSkuId) {
      urlParams.set(QUERY_PARAM_KEY_FOR_SKU, currentSkuId);
      shouldUpdateUrl = true;
    }
  } else if (parentId !== currentSkuId) {
    // * This will prevent un-necessary updated in case of simple products
    urlParams.append(QUERY_PARAM_KEY_FOR_SKU, currentSkuId);
    shouldUpdateUrl = true;
  }

  if (shouldUpdateUrl) {
    const updatedQueryString = urlParams.toString();
    const updatedProductUrl = appendQueryStringToUrl(
      pathname,
      updatedQueryString
    );
    /**
     * ! MUST USE "history.replace"
     *   if history.push is used, the physical back click will just take the user back to previously selected shade.
     */
    history.replace(updatedProductUrl);
  }
};

export const getTabsValues = (
  description: string,
  ingredients: string,
  howToUse: string
) => {
  let count = -1;
  let tabHeaders: TabHeaders = {};
  let tabsData: TabsData = {};
  if (description && description.length > MINIMUM_DESCRIPTION_LENGTH) {
    count += 1;
    tabHeaders[DESCRIPTION_TAB] = count;
    // id is added bcz on description tab need to add expiry and mrp data also.
    tabsData[count] = { id: DESCRIPTION_TAB, value: description };
  }
  if (ingredients && ingredients.length > MINIMUM_DESCRIPTION_LENGTH) {
    count += 1;
    tabHeaders[INGREDIENTS_TAB] = count;
    tabsData[count] = { id: INGREDIENTS_TAB, value: ingredients };
  }
  if (howToUse && howToUse.length > MINIMUM_DESCRIPTION_LENGTH) {
    count += 1;
    tabHeaders[HOW_TO_USE_TAB] = count;
    tabsData[count] = { id: HOW_TO_USE_TAB, value: howToUse };
  }
  return { tabsData: tabsData, tabHeaders: tabHeaders };
};

export const isProProduct = (proFlag: number, isProUser: number) => {
  if (isProUser && (proFlag === 1 || proFlag === 2)) {
    return true;
  }

  return false;
};

export const trackClick = (event) => {
  pushEvent(`${event}_CLICK`, {});
};
